<script setup>
import { t } from "@/controllerLK/AvalLocalization";
import FilterIcon from "@/components/Icons/FilterIcon.vue";
import {inject, onMounted, ref, defineEmits, computed} from "vue";
import DatetimeInput from "@/components/MapPage/MapNavbar/Filters/DatetimeInput.vue";
import GoodIcon from "@/components/Icons/GoodIcon.vue";
import SadIcon from "@/components/Icons/SadIcon.vue";
import NeutralIcon from "@/components/Icons/NeutralIcon.vue";
import {importanceList} from "@/const/importance.list";
import {startOfDayUTC3, endOfDayUTC3} from "@/tools/date-time-tools";

const emit = defineEmits(["update-filter"]);
const isShowDropdown = ref(false);

const selectedDateFilter       = inject("selectedDateFilter");
const selectedColorList        = inject("selectedColorList");
const selectedSentimentFilter  = inject("selectedSentimentFilter");
const activeFilterTypeOption   = inject("activeFilterTypeOption");

// Список дат для выбора
const dateFilterList = ref([
  {
    name: t("datetime_filter.date.last_day"),
    value: startOfDayUTC3(Math.floor(Date.now() / 1000) - 24 * 3600),
  },
  {
    name: t("datetime_filter.date.last_three_day"),
    value: startOfDayUTC3(Math.floor(Date.now() / 1000) - 72 * 3600),
  },
  {
    name: t("datetime_filter.date.last_week"),
    value: startOfDayUTC3(Math.floor(Date.now() / 1000) - 168 * 3600),
  }
]);

onMounted(() => {
  selectedDateFilter.value.dateFrom = dateFilterList.value[0].value;
  selectedDateFilter.value.dateTo   = endOfDayUTC3(Math.floor(Date.now() / 1000));

  // Нормализация selectedSentimentFilter из localStorage:
  selectedSentimentFilter.value = selectedSentimentFilter.value
      .map(saved =>
          sentimentFilterList.value.find(s => s.value === saved.value)
      )
      .filter(Boolean);
});

// Список важности
const getImportanceList = computed(() => {
  return Object.values(importanceList).filter((item) => item.importance !== null && item.importance !== 0);
});

// Является ли переданная важность активной
const isColorActive = color =>
    selectedColorList.value.some(c => c === color.importance);


// Функция обновления фильтра.
const updateFilter = () => {
  emit("update-filter");
};

// Список сантиментов
const sentimentFilterList = ref([
  { name: t("datetime_filter.sentiment.good"),   value: 1 },
  { name: t("datetime_filter.sentiment.neutral"),  value: 0 },
  { name: t("datetime_filter.sentiment.bad"),   value: -1 },
]);

// Является ли переданный сантимент активным
const isSentimentActive = s =>
    selectedSentimentFilter.value.some(x => x.value === s.value);


// Переключение типа фильтра
const updateActiveFilterTypeOption = value => {
  activeFilterTypeOption.value = value;
  emit("update-filter");
};

// Является ли дата из списка активной
const isDateListItemActive = (timestamp1, timestamp2) => {
  const date1 = new Date(timestamp1 * 1000);
  const date2 = new Date(timestamp2 * 1000);

  return (
      date1.getFullYear() === date2.getFullYear() &&
      date1.getMonth() === date2.getMonth() &&
      date1.getDate() === date2.getDate()
  );
};

const activeDate = computed(() => {
  const savedDateFrom = selectedDateFilter.value.dateFrom;
  const activeItem = dateFilterList.value.find(item =>
      isDateListItemActive(item.value, savedDateFrom)
  );
  return activeItem ? activeItem.value : null;
});


const selectDate = item => {
  selectedDateFilter.value.dateFrom = item.value;
  selectedDateFilter.value.dateTo   = endOfDayUTC3(Math.floor(Date.now() / 1000));
  emit("update-filter");
};
</script>

<template>
  <SimpleDropdown
    max-width="271px"
    teleport="#app"
    :use-searchbar="false"
    :title="t('datetime_filter.label.date_filter')"
    :is-visible="isShowDropdown"
    @close="isShowDropdown = false"
  >
    <template #title-icon>
      <FilterIcon />
    </template>

    <template #trigger>
      <AvalButtonFixedTertiary
        :is-active="isShowDropdown"
        size="large"
        :icon-only="true"
        @click="isShowDropdown = !isShowDropdown"
      >
        <FilterIcon />
      </AvalButtonFixedTertiary>
    </template>

    <template #dropdown-item>
      <div class="filter-dropdown__calendar-wrapper">
        <!-- Календарь -->
        <DatetimeInput
          style="height: 38px;"
          class="filter-dropdown__calendar"
          @update-filter="emit('update-filter')"
        />

        <!-- Список фиксированных дат -->
        <AvalListView>
          <AvalListItem
            v-for="item in dateFilterList"
            :key="item.value"
            :model-value="activeDate"
            :item="item.value"
            @click="selectDate(item)"
          >
            {{ item.name }}
          </AvalListItem>
        </AvalListView>

        <!-- Важность / Сантимент -->
        <div class="filter-dropdown__switcher">
          <div class="filter-dropdown__switcher-labels">
            <div
              class="filter-dropdown__switcher-tab"
              :class="{ 'filter-dropdown__switcher-tab--active': activeFilterTypeOption === 'importance'}"
              @click="updateActiveFilterTypeOption('importance')"
            >
              {{ t('datetime_filter.label.importance_filter') }}
            </div>
            <div
              class="filter-dropdown__switcher-tab"
              :class="{ 'filter-dropdown__switcher-tab--active': activeFilterTypeOption === 'sentiment'}"
              @click="updateActiveFilterTypeOption('sentiment')"
            >
              {{ t('datetime_filter.label.sentiment_filter') }}
            </div>
          </div>

          <ul class="filter-dropdown__switcher-content">
            <template v-if="activeFilterTypeOption === 'importance'">
              <li
                v-for="item in getImportanceList"
                :key="item.name"
              >
                <AvalCheckbox
                  v-model="selectedColorList"
                  :value="item.importance"
                  class="filter-dropdown__item"
                  :class="{ 'filter-dropdown__item--active': isColorActive(item) }"
                  @change="updateFilter"
                >
                  <div
                    class="filter-dropdown__color-circle"
                    :style="{ background: item.color }"
                  />
                  {{ t(`datetime_filter.importance.${item.name}`) }}
                </AvalCheckbox>
              </li>
            </template>


            <template v-else>
              <li
                v-for="item in sentimentFilterList"
                :key="item.value"
              >
                <AvalCheckbox
                  v-model="selectedSentimentFilter"
                  :value="item"
                  class="filter-dropdown__item"
                  :class="{ 'filter-dropdown__item--active': isSentimentActive(item) }"
                  @change="updateFilter"
                >
                  <Component
                    :is="item.value > 0 ? GoodIcon : item.value < 0 ? SadIcon : NeutralIcon"
                    class="filter-dropdown__item-sentiment"
                  />
                  {{ item.name }}
                </AvalCheckbox>
              </li>
            </template>
          </ul>
        </div>
      </div>
    </template>
  </SimpleDropdown>
</template>

<style lang="scss" scoped>
.filter-dropdown {
  position: relative;

  &__item {
    display: flex;
    flex-wrap: nowrap;
    padding: 6px 14px;
    border-radius: 8px;
    gap: 8px;
    height: 30px;
    width: 100%;
    font-size: 14px;
    line-height: 18px;
    color: var(--text-text-main);

    :deep(.checkbox__label) {
      display: flex;
      gap: 8px;
      color: inherit;
    }


    &-sentiment {
      width: 17px;
      height: 17px;
    }

    &:hover {
      cursor: pointer;
      user-select: none;
      background: var(--background-bg-hover);
    }

    &--active {
      color: #1F9AFF;
      &:hover {
        background: #E0F0FF;
      }
    }
  }

  &__calendar {
    display: flex;
    height: 38px;
    line-height: 20px;
  }

  &__calendar-wrapper {
    display: flex;
    flex-direction: column;
    gap: 4px;
  }

  &__calendar-list {
    display: flex;
    flex-direction: column;
  }

  &__switcher {
    display: flex;
    flex-direction: column;
    gap: 10px;

    &-content {
      display: flex;
      flex-direction: column;
      gap: 6px;
    }

    &-labels {
      display: flex;
      justify-content: space-between;
      gap: 6px;
      background: var(--background-01);
      padding: 4px;
      border-radius: 10px;
    }

    &-tab {
      display: flex;
      align-items: center;
      justify-content: center;
      padding: 6px 8px;
      border-radius: 8px;
      color: rgba(26, 100, 212, 0.2);
      font-weight: 500;
      cursor: pointer;
      user-select: none;
      line-height: 18px;
      flex: 1 1 0;

      &--active {
        background: var(--blue-blue40);
        color: var(--text-text-on);
      }
    }
  }

  &__color-circle {
    width: 16px;
    height: 16px;
    border-radius: 50%;
  }
}
</style>